import type { Notice } from "../../services/api/types";
import { dashboardStatsQuery, noticesQuery } from "../../services/queries";
import { mapNoticesToCalendarEventCores } from "../../utils/noticeCalendarEvents";
import { formatRecurrenceFrequency, formatRecurrenceUntil, resolveNoticeEventRecurrence } from "../../utils/eventRecurrenceDisplay";
import type { AnswerContext } from "./catalogue";
import { loadCircles } from "./catalogue/pickCircle";
import { viewQuery } from "./scopedQuery";

type Upcoming = { title: string; notice: Notice };

/** The first Event on Home's Upcoming Events card, the same one that answer names. */
async function firstUpcoming(ctx: AnswerContext): Promise<Upcoming | null> {
  const orgId = ctx.user?.schoolId || ctx.user?.orgId;
  const [notices, stats] = await Promise.all([
    ctx.queryClient.fetchQuery(viewQuery(ctx.user, noticesQuery(orgId, ctx.token))),
    ctx.queryClient.fetchQuery(viewQuery(ctx.user, dashboardStatsQuery(orgId, ctx.token))),
  ]);
  const count = stats.upcomingEvents || 0;
  if (!count) return null;
  const now = new Date();
  const next = mapNoticesToCalendarEventCores(notices)
    .filter((event) => event.end > now)
    .sort((a, b) => a.start.getTime() - b.start.getTime())[0];
  if (!next) return null;
  return { title: next.fullTitle, notice: next.notice };
}

/** "BlackBrick Umhlanga Launch repeats weekly, until Sunday, January 31, 2027." */
export function recurrenceLine(notice: Notice): string {
  if (notice.type !== "event") return `${notice.title} isn't an Event, so it doesn't repeat.`;
  const rule = resolveNoticeEventRecurrence(notice);
  if (!rule && !notice.eventSeriesId) return `${notice.title} doesn't repeat.`;
  const how = formatRecurrenceFrequency(rule?.frequency)?.toLowerCase();
  const until = formatRecurrenceUntil(rule?.until);
  return `${notice.title} repeats${how ? ` ${how}` : ""}${until ? `, until ${until}` : ""}.`;
}

/** Where an Event is, or that no place is set. The title and the time are not the place. */
export function placeOf(title: string, notice: Notice): string {
  const place = notice.eventLocation?.trim();
  if (place) return `The Event ${title} is at ${place}.`;
  return `No location is set for the Event ${title}. The title and the time aren't the place.`;
}

/**
 * Who an Event is for: the internal team, its Circle by name, one person, or Everyone when it names no one.
 * `circles` turns the Circle it went to into its name; an unknown one is only "a Circle".
 */
export function audienceOf(title: string, notice: Notice, shownOn: string, circles: { circleId: string; name: string }[] = []): string {
  // "The Event Debs Ball is for …", or "Kara's Birthday went to …" for a Notice that isn't an Event
  const isEvent = ["event", "custom_event"].includes(String(notice.type ?? "")) || Boolean(notice.eventStartDate);
  const subject = isEvent ? `The Event ${title} is for` : `${title} went to`;
  if (notice.staffOnly) return `${subject} the internal team.`;
  const circle = notice.targetSubcircle?.trim();
  if (circle) {
    const name = circles.find((c) => c.circleId === circle)?.name;
    return name ? `${subject} ${name}.` : `${subject} one Circle. ${shownOn} doesn't name it.`;
  }
  if (notice.targetUserId) return `${subject} one person. ${shownOn} doesn't name them.`;
  return `${subject} Everyone.`;
}

/** Where that Event is, or that no place is set. */
export async function upcomingPlace(ctx: AnswerContext): Promise<string> {
  const upcoming = await firstUpcoming(ctx);
  if (!upcoming) return "Nothing is coming up, so there is no place to give.";
  return placeOf(upcoming.title, upcoming.notice);
}

/** Who that Event is for, or that the Home card doesn't say. */
export async function upcomingAudience(ctx: AnswerContext): Promise<string> {
  const upcoming = await firstUpcoming(ctx);
  if (!upcoming) return "Nothing is coming up, so there isn't an audience to name.";
  const circles = await loadCircles(ctx).catch(() => []);
  return audienceOf(upcoming.title, upcoming.notice, "the Home card", circles);
}
